import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import gsap from 'gsap';
import { MapPin, ShieldCheck, HardDrive, Cpu, Server, Zap, Globe } from 'lucide-react';
import { citiesData } from '../data/cities';

const CityPage = () => {
    const { cityId } = useParams();
    const cityData = citiesData[cityId];
    const containerRef = useRef(null);

    useEffect(() => {
        if (!cityData) return;

        window.scrollTo(0, 0);

        const ctx = gsap.context(() => {
            gsap.from('.city-reveal', {
                y: 30,
                opacity: 0,
                duration: 1,
                stagger: 0.15,
                ease: 'power3.out'
            });
        }, containerRef);

        return () => ctx.revert();
    }, [cityId, cityData]);

    if (!cityData) {
        return <Navigate to="/locations" replace />;
    }

    return (
        <div ref={containerRef} className="pt-32 pb-24 bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4]">

            {/* Hero Section */}
            <section className="max-w-6xl mx-auto px-6 mb-24">
                <div className="text-center mb-16 max-w-4xl mx-auto city-reveal">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#18181B] border border-[#F0EFF4]/10 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6">
                        <MapPin className="w-4 h-4" />
                        ACTIVE DEPLOYMENT ZONE
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                        RoofClaw Systems:<br />Deployed in <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl md:text-7xl">{cityData.name}</span>
                    </h1>
                    <p className="text-xl text-[#F0EFF4]/70 mb-8 font-light">
                        {cityData.heroSubtitle}
                    </p>
                </div>

                {/* Local Context Context */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24 bg-[#18181B] p-8 md:p-12 rounded-[3rem] border border-[#F0EFF4]/10 city-reveal relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF]/5 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>

                    <div>
                        <h3 className="text-2xl font-bold mb-4">The {cityData.name} Operator's Edge</h3>
                        <p className="text-[#F0EFF4]/70 mb-8 leading-relaxed text-lg">
                            {cityData.localContext}
                        </p>

                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-sm font-data tracking-wider bg-[#0A0A14] p-4 rounded-xl border border-[#F0EFF4]/5">
                                <Zap className="w-5 h-5 text-[#10B981]" />
                                <span className="uppercase">Market Threat: {cityData.weatherFocus}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm font-data tracking-wider bg-[#0A0A14] p-4 rounded-xl border border-[#F0EFF4]/5">
                                <ShieldCheck className="w-5 h-5 text-[#7B61FF]" />
                                <span className="uppercase">Strategic Advantage: {cityData.marketFocus}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#0A0A14] rounded-[2rem] border border-[#F0EFF4]/5 p-8 flex flex-col justify-between min-h-[400px] relative overflow-hidden group">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2220%22 height=%2220%22 viewBox=%220 0 20 20%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Ccircle cx=%222%22 cy=%222%22 r=%221%22 fill=%22%237b61ff%22 fill-opacity=%220.05%22/%3E%3C/svg%3E')] [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
                        <div className="relative z-10 flex-1">
                            <div className="flex items-center justify-between border-b border-[#F0EFF4]/10 pb-4 mb-6">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-[#7B61FF]" />
                                    <span className="font-data text-xs tracking-widest text-[#F0EFF4]/60">TAILSCALE NODE STATUS</span>
                                </div>
                                <span className="font-data text-xs text-[#10B981] flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></div> ACTIVE</span>
                            </div>

                            <div className="font-mono text-sm text-[#F0EFF4]/60 space-y-3">
                                <p><span className="text-[#7B61FF]">&gt;</span> location ping: {cityData.name}</p>
                                <p><span className="text-[#7B61FF]">&gt;</span> latency: 12ms</p>
                                <p><span className="text-[#7B61FF]">&gt;</span> encryption: AES-256 (WireGuard)</p>
                                <p><span className="text-[#7B61FF]">&gt;</span> local_override: enabled</p>
                                <div className="mt-8 pt-6 border-t border-[#F0EFF4]/10">
                                    <p className="text-[#10B981]">SYSTEM SECURE. READY FOR DEPLOYMENT.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24 city-reveal">
                    <div className="bg-[#18181B] p-8 rounded-[2rem] border border-[#F0EFF4]/5">
                        <HardDrive className="w-8 h-8 text-[#7B61FF] mb-6" />
                        <h4 className="text-xl font-bold mb-3">1. Built Offline</h4>
                        <p className="text-sm text-[#F0EFF4]/60">Your machine is securely configured, locked, and encrypted in our Kelowna, BC lab.</p>
                    </div>
                    <div className="bg-[#18181B] p-8 rounded-[2rem] border border-[#F0EFF4]/5">
                        <MapPin className="w-8 h-8 text-[#7B61FF] mb-6" />
                        <h4 className="text-xl font-bold mb-3">2. Shipped Direct</h4>
                        <p className="text-sm text-[#F0EFF4]/60">The hardware is overnighted directly to your {cityData.name} headquarters or preferred location.</p>
                    </div>
                    <div className="bg-[#18181B] p-8 rounded-[2rem] border border-[#F0EFF4]/5">
                        <Cpu className="w-8 h-8 text-[#7B61FF] mb-6" />
                        <h4 className="text-xl font-bold mb-3">3. Plug & Play</h4>
                        <p className="text-sm text-[#F0EFF4]/60">Plug into power and ethernet. The secure mesh network handshakes automatically.</p>
                    </div>
                </div>

                <div className="text-center city-reveal">
                    <h2 className="text-3xl font-bold mb-6">Secure Your Node in {cityData.name}</h2>
                    <p className="text-[#F0EFF4]/60 mb-10 max-w-xl mx-auto">
                        Only 5 configurations govern our monthly deployment schedule. Secure your position and begin the 7-day protocol.
                    </p>
                    <Link to="/#reserve" className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-[#7B61FF] text-[#F0EFF4] font-bold tracking-wide hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]">
                        Reserve Build Spot
                    </Link>
                </div>

            </section>
        </div>
    );
};

export default CityPage;
